import { Code, Redo, Undo } from '@mui/icons-material';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatColorTextIcon from '@mui/icons-material/FormatColorText';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import ImageIcon from '@mui/icons-material/Image';
import PivotTableChartIcon from '@mui/icons-material/PivotTableChart';
import TableViewIcon from '@mui/icons-material/TableView';
import { styled } from '@mui/material';
import { EditorView } from '@tiptap/pm/view';
import { EditorContent } from '@tiptap/react';

import WsColors from '../../WsColors';
import { Item } from '../Item';

const IconStyles = {
  cursor: 'pointer',
  ':hover': {
    backgroundColor: WsColors.White,
  },
};
export const BoldIcon = styled(FormatBoldIcon)(() => IconStyles);

export const ColorTextIcon = styled(FormatColorTextIcon)(() => IconStyles);

export const ColorHighlightIcon = styled(BorderColorIcon)(() => IconStyles);

export const ItalicIcon = styled(FormatItalicIcon)(() => IconStyles);

export const LeftAlignIcon = styled(FormatAlignLeftIcon)(() => IconStyles);

export const CenterAlignIcon = styled(FormatAlignCenterIcon)(() => IconStyles);

export const RightAlignIcon = styled(FormatAlignRightIcon)(() => IconStyles);

export const UndoIcon = styled(Undo)(() => IconStyles);

export const RedoIcon = styled(Redo)(() => IconStyles);

export const CodeIcon = styled(Code)(() => IconStyles);

export const TableView = styled(TableViewIcon)(() => IconStyles);

export const TableOptions = styled(PivotTableChartIcon)(() => IconStyles);

export const BulletPointsIcon = styled(FormatListBulletedIcon)(() => IconStyles);

export const ImageUploadIcon = styled(ImageIcon)(() => IconStyles);

export const EditorToolbar = styled(Item)(({ theme }) => ({
  backgroundColor: WsColors.TertiaryShades100,
  padding: theme.spacing(1.5),
  borderRadius: theme.spacing(0),

  display: 'flex',
  overflowX: 'auto',
  whiteSpace: 'nowrap',
  gap: theme.spacing(2),
  scrollbarWidth: 'thin',
  '&::-webkit-scrollbar': {
    height: '6px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: WsColors.ToolbarColor,
    borderRadius: '10px',
  },
}));

export const CustomEditorContent = styled(EditorContent)(({ theme }) => ({
  minHeight: '100%',
  height: 'fit-content',
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),

  '.ProseMirror:focus': {
    border: 'none',
    outline: 'none',
  },

  '.ProseMirror img': {
    maxWidth: '100%',
    height: 'auto',
  },

  '.ProseMirror': {
    minHeight: '400px',
    paddingBottom: '100px',
    cursor: 'text',
  },

  '.ProseMirror p:empty::before': {
    content: "' '",
    display: 'inline-block',
    height: '1em',
  },
}));

export const HiddenInput = styled('input')({
  display: 'none',
});

export const filterHref = (href: string) => {
  const array: string[] = [
    '{{candidate.name}}',
    '{{candidate.email}}',
    '{{candidate.phoneNumber}}',
    '{{candidate.source}}',
    '{{candidate.currentLocation.city}}',
    '{{candidate.currentLocation.state}}',
    '{{candidate.currentLocation.country}}',
    '{{candidate.totalYearsExperience}}',
    '{{candidate.relevantYearsExperience}}',
    '{{candidate.previousJobTitles}}',
    '{{candidate.previousEmployers}}',
    '{{candidate.currentJobTitle}}',
    '{{candidate.currentJobType}}',
    '{{candidate.expectedSalaryCurrency}}',
    '{{candidate.expectedBaseSalary}}',
    '{{candidate.expectedBaseSalaryTimeSpan | splitByUpperCase}}',
    '{{candidate.jobPreferencePermanent}}',
    '{{candidate.jobPreferenceContract}}',
    '{{candidate.currentSalaryCurrency}}',
    '{{candidate.currentSalary}}',
    '{{candidate.currentSalaryTimeSpan | splitByUpperCase}}',
    '{{candidate.currentEmployer}}',
    '{{candidate.otherCurrentAllowances}}',
    '{{candidate.otherExpectedAllowances}}',
    '{{candidate.reasonForNewJob}}',
    '{{candidate.relocationPreference | splitByUpperCase}}',
    "{{candidate.willingToTravel == null ? 'N/A' : (candidate.willingToTravel ? 'Yes' : 'No')}}",
    '{{candidate.familyWillingToRelocate}}',
    '{{candidate.interviewingWithOthers}}',
    '{{candidate.messengers}}',
    '{{candidate.skills}}',
    '{{candidate.assessment}}',
    '{{candidate.workAuthorization}}',
    '{{job.title}}',
    '{{job.jobCode}}',
    '{{job.clientJobCode}}',
    '{{job.jobType}}',
    '{{job.description}}',
    '{{job.tags}}',
    '{{job.skills}}',
    '{{job.companyName}}',
    '{{job.salaryCurrency}} {{job.minimumSalary}} - {{job.salaryCurrency}} {{job.maximumSalary}} {{job.salaryTimeSpan | splitByUpperCase}}',
    '[{{job.locations_city_and_state}}]',
    '[{{job.locations_city_and_country}}]',
    '{{job.isRemote}}',
    '{{job.jobUrl}}',
    '{{recruiter.firstName}} {{recruiter.lastName}}',
    '{{recruiter.email}}',
    '{{recruiter.phoneNumber}}',
    '{{candidate.linkedInURL}}',
    '{{candidate.education}}',
    '{{candidate.reasonForNewJob}}',
    '{{candidate.workspaceName}}',
  ];
  return array.indexOf(href) != -1;
};

export const handleImageUpload = async (
  file: File,
  view: EditorView,
  onImageUpload?: (file: File) => Promise<string>,
  pos?: number
) => {
  const insertImage = (src: string) => {
    const { tr } = view.state;
    const imageNode = view.state.schema.nodes.image.create({ src });
    view.dispatch(
      pos !== undefined ? tr.insert(pos, imageNode) : tr.replaceSelectionWith(imageNode)
    );
  };

  if (onImageUpload) {
    const url = await onImageUpload(file);
    insertImage(url);
  } else {
    const reader = new FileReader();
    reader.onload = (e) => {
      const url = e.target?.result as string;
      insertImage(url);
    };
    reader.readAsDataURL(file);
  }
};

export const createEditorProps = (onImageUpload?: (file: File) => Promise<string>) => ({
  attributes: {
    class: 'tiptap ProseMirror',
  },
  handleClick(view: EditorView) {
    view.focus();
    return false;
  },
  handlePaste: (view: EditorView, event: ClipboardEvent) => {
    const items = Array.from(event.clipboardData?.items || []);
    const imageItem = items.find((item) => item.type.startsWith('image/'));

    if (imageItem) {
      event.preventDefault();
      const blob = imageItem.getAsFile();
      if (blob) {
        handleImageUpload(blob, view, onImageUpload);
        return true;
      }
    }
    return false;
  },

  handleDrop: (view: EditorView, event: DragEvent, _slice: unknown, moved: boolean) => {
    if (!moved && event.dataTransfer?.files?.length) {
      const imageFile = Array.from(event.dataTransfer.files).find((file) =>
        file.type.startsWith('image/')
      );

      if (imageFile) {
        event.preventDefault();
        const pos = view.posAtCoords({
          left: event.clientX,
          top: event.clientY,
        })?.pos;

        handleImageUpload(imageFile, view, onImageUpload, pos);
        return true;
      }
    }
    return false;
  },
});

export const injectColgroupAndFixWidthsForAllTables = (html: string): string => {
  const container = document.createElement('div');
  container.innerHTML = html;

  const tables = container.querySelectorAll('table');
  if (!tables.length) return html;

  tables.forEach((table) => {
    const firstRow = table.querySelector('tr');
    let totalWidth = 0;
    const colWidths: string[] = [];

    if (firstRow) {
      firstRow.querySelectorAll('th, td').forEach((cell) => {
        const width = cell.getAttribute('colwidth');
        if (width) {
          colWidths.push(width);
          totalWidth += parseInt(width, 10);
        }
      });
    }

    // Table attributes and styling
    table.setAttribute('cellspacing', '0');
    table.setAttribute('cellpadding', '0');
    table.setAttribute('border', '0');
    table.setAttribute('width', totalWidth.toString());

    Object.assign(table.style, {
      width: `${totalWidth}px`,
      minWidth: `${totalWidth}px`,
      maxWidth: `${totalWidth}px`,
      tableLayout: 'fixed',
      margin: '0',
      borderCollapse: 'separate',
      borderSpacing: '0px',
      border: '2px solid #ced4da',
      boxShadow: '2px 0 0 0 #ced4da',
    });

    // Colgroup
    table.querySelector('colgroup')?.remove();
    if (colWidths.length) {
      const colgroup = document.createElement('colgroup');
      colWidths.forEach((w) => {
        const col = document.createElement('col');
        col.setAttribute('width', w);
        Object.assign(col.style, {
          width: `${w}px`,
          minWidth: `${w}px`,
          maxWidth: `${w}px`,
        });
        colgroup.appendChild(col);
      });

      const tbody = table.querySelector('tbody');
      if (tbody) {
        table.insertBefore(colgroup, tbody);
      } else {
        table.insertBefore(colgroup, table.firstChild);
      }
    }

    // Cell styling
    table.querySelectorAll('td, th').forEach((cell) => {
      const htmlCell = cell as HTMLElement;
      const colWidth = cell.getAttribute('colwidth') || '200';
      cell.setAttribute('width', colWidth);

      Object.assign(htmlCell.style, {
        width: `${colWidth}px`,
        minWidth: `${colWidth}px`,
        maxWidth: `${colWidth}px`,
        border: '2px solid #ced4da',
        padding: '3px 5px',
        verticalAlign: 'top',
        boxSizing: 'border-box',
        wordBreak: 'break-word',
        whiteSpace: 'normal',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      });

      htmlCell.style.removeProperty('overflow-wrap');
      htmlCell.style.removeProperty('position');
    });
  });

  return container.innerHTML;
};

export const wrapAllTablesInFixedContainers = (html: string): string => {
  const container = document.createElement('div');
  container.innerHTML = html;

  const tables = container.querySelectorAll('table');
  if (!tables.length) return html;

  tables.forEach((table) => {
    const firstRow = table.querySelector('tr');
    let totalWidth = 0;

    if (firstRow) {
      firstRow.querySelectorAll('th, td').forEach((cell) => {
        const width = cell.getAttribute('colwidth');
        if (width) {
          totalWidth += parseInt(width, 10);
        }
      });
    }

    const buffer = 2;
    const wrapper = document.createElement('div');
    Object.assign(wrapper.style, {
      width: `${totalWidth + buffer}px`,
      minWidth: `${totalWidth + buffer}px`,
      maxWidth: `${totalWidth + buffer}px`,
      overflow: 'hidden',
      display: 'inline-block',
      boxSizing: 'border-box',
    });

    table.replaceWith(wrapper);
    wrapper.appendChild(table);
  });

  // Process all wrapped tables
  const enhancedHtml = injectColgroupAndFixWidthsForAllTables(container.innerHTML);
  return enhancedHtml;
};
